import React, { useState, useEffect } from "react";
import { BadgeCheck, Mic, X, Send, Crown, Loader2, ExternalLink, AlertCircle } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";

/**
 * Apple Music / Spotify-for-Artists style "Claim this artist page" flow.
 * Embedded on the public `/artist/:id` page. Renders one of:
 *   - "Claim this page" button → opens the modal form
 *   - "Claim under review" badge when status=pending
 *   - "You manage this page" verified badge when status=approved
 *   - Read-only Verified badge when ANOTHER user has claimed
 */
const ClaimArtistDialog = ({ itunesArtistId, artistName, applePicked, onVerified }) => {
  const { user, login } = useAuth();
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [status, setStatus] = useState(null);   // { claimed, verified, owner, my_claim }
  const [form, setForm] = useState({
    official_email: "",
    apple_music_url: applePicked || "",
    social_url: "",
    statement: "",
  });

  useEffect(() => {
    let cancel = false;
    if (!itunesArtistId) return;
    api.get(`/profile/claim-status/${itunesArtistId}`)
      .then((r) => { if (!cancel) setStatus(r.data); })
      .catch(() => { if (!cancel) setStatus(null); });
    return () => { cancel = true; };
  }, [itunesArtistId, user?.user_id]);

  const submit = async (e) => {
    e?.preventDefault?.();
    if (!user) { login(); return; }
    if (!form.official_email.includes("@")) {
      toast.error("A valid email is required");
      return;
    }
    setBusy(true);
    try {
      const r = await api.post("/profile/claim", {
        itunes_artist_id: itunesArtistId,
        artist_name: artistName,
        apple_music_url: form.apple_music_url,
        official_email: form.official_email,
        social_url: form.social_url,
        statement: form.statement,
      });
      if (r.data?.verified) {
        toast.success("You're now verified", { description: r.data?.message || "" });
        onVerified?.();
      } else {
        toast.success("Claim submitted", { description: r.data?.message || "" });
      }
      // Refresh status
      const s = await api.get(`/profile/claim-status/${itunesArtistId}`);
      setStatus(s.data);
      setOpen(false);
    } catch (err) {
      const detail = err?.response?.data?.detail || "Claim failed";
      toast.error(detail);
    } finally {
      setBusy(false);
    }
  };

  // ===== Verified by current user =====
  const myStatus = status?.my_claim?.status;
  if (status?.verified && status?.owner?.user_id === user?.user_id) {
    return (
      <span
        data-testid="claim-manage-badge"
        className="inline-flex items-center gap-2 bg-[#E2FF31]/15 border border-[#E2FF31]/40 text-[#E2FF31] font-semibold rounded-full px-4 py-2 text-sm"
      >
        <BadgeCheck size={16} /> You manage this page
      </span>
    );
  }

  // ===== Verified by someone else (read-only) =====
  if (status?.verified && status?.owner?.user_id !== user?.user_id) {
    return (
      <span
        data-testid="claim-verified-badge"
        className="inline-flex items-center gap-2 bg-blue-500/15 border border-blue-500/40 text-blue-200 font-semibold rounded-full px-4 py-2 text-sm"
        title={`Verified by ${status?.owner?.display_name || "the artist"}`}
      >
        <BadgeCheck size={16} /> Verified
      </span>
    );
  }

  // ===== Current user has a pending claim =====
  if (myStatus === "pending") {
    return (
      <span
        data-testid="claim-pending-badge"
        className="inline-flex items-center gap-2 bg-amber-500/15 border border-amber-500/40 text-amber-300 font-semibold rounded-full px-4 py-2 text-sm"
      >
        <Loader2 size={14} className="animate-spin" /> Claim under review
      </span>
    );
  }

  // ===== Default: Claim button =====
  return (
    <>
      <button
        type="button"
        data-testid="claim-page-btn"
        onClick={() => { if (!user) { login(); return; } setOpen(true); }}
        className="inline-flex items-center gap-2 bg-white/[0.06] hover:bg-white/[0.12] border border-white/15 hover:border-white/30 text-white font-semibold rounded-full px-5 py-2.5 text-sm transition"
      >
        <Mic size={14} /> Claim this page
      </button>

      {open && (
        <div
          className="fixed inset-0 z-[100] flex items-end sm:items-center justify-center bg-black/70 backdrop-blur-sm p-4"
          onClick={() => !busy && setOpen(false)}
          data-testid="claim-modal"
        >
          <div
            className="w-full max-w-lg bg-[#0c0c0d] border border-white/10 rounded-3xl shadow-2xl overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="relative px-6 pt-6 pb-4 bg-gradient-to-br from-[#FF2E00]/15 via-[#FF2E00]/10 to-[#E2FF31]/5">
              <button
                type="button"
                onClick={() => setOpen(false)}
                disabled={busy}
                className="absolute right-4 top-4 text-white/50 hover:text-white"
                aria-label="Close"
              >
                <X size={18} />
              </button>
              <p className="text-[11px] uppercase tracking-[0.3em] text-white/60 font-semibold flex items-center gap-2">
                <Mic size={11} className="text-[#E2FF31]" /> Artist verification
              </p>
              <h2 className="font-display text-2xl sm:text-3xl font-semibold mt-2 leading-tight">
                Are you {artistName}?
              </h2>
              <p className="text-white/65 text-sm mt-2">
                Claim this artist page to add a bio, upload songs, track upcoming
                releases, and earn $0.20 per stream.
              </p>
              <div className="mt-3 inline-flex items-center gap-2 bg-amber-500/10 border border-amber-500/30 text-amber-300 rounded-full px-3 py-1 text-[11px]">
                <Loader2 size={11} /> Admin reviews each claim within 24–48 hours
              </div>
            </div>

            <form onSubmit={submit} className="px-6 pb-6 space-y-4 mt-2">
              <div>
                <label className="block text-xs font-semibold uppercase tracking-wider text-white/55 mb-1.5">
                  Official email <span className="text-[#FF2E00]">*</span>
                </label>
                <input
                  data-testid="claim-email-input"
                  type="email"
                  required
                  value={form.official_email}
                  onChange={(e) => setForm((p) => ({ ...p, official_email: e.target.value }))}
                  placeholder="press@yourdomain.com"
                  className="w-full bg-white/[0.05] border border-white/10 focus:border-[#FF2E00] rounded-xl px-4 py-2.5 text-sm outline-none transition"
                />
                <p className="text-[11px] text-white/40 mt-1">
                  Use an email at your official domain or label so we can verify identity.
                </p>
              </div>

              <div>
                <label className="block text-xs font-semibold uppercase tracking-wider text-white/55 mb-1.5">
                  <ExternalLink size={11} className="inline mr-1" /> Apple Music artist URL
                </label>
                <input
                  data-testid="claim-apple-input"
                  type="url"
                  value={form.apple_music_url}
                  onChange={(e) => setForm((p) => ({ ...p, apple_music_url: e.target.value }))}
                  placeholder="https://music.apple.com/us/artist/..."
                  className="w-full bg-white/[0.05] border border-white/10 focus:border-[#FF2E00] rounded-xl px-4 py-2.5 text-sm outline-none transition font-mono"
                />
              </div>

              <div>
                <label className="block text-xs font-semibold uppercase tracking-wider text-white/55 mb-1.5">
                  Official social URL (optional)
                </label>
                <input
                  data-testid="claim-social-input"
                  type="url"
                  value={form.social_url}
                  onChange={(e) => setForm((p) => ({ ...p, social_url: e.target.value }))}
                  placeholder="https://instagram.com/yourhandle"
                  className="w-full bg-white/[0.05] border border-white/10 focus:border-[#FF2E00] rounded-xl px-4 py-2.5 text-sm outline-none transition font-mono"
                />
              </div>

              <div>
                <label className="block text-xs font-semibold uppercase tracking-wider text-white/55 mb-1.5">
                  Anything else?
                </label>
                <textarea
                  data-testid="claim-statement-input"
                  rows={3}
                  maxLength={600}
                  value={form.statement}
                  onChange={(e) => setForm((p) => ({ ...p, statement: e.target.value }))}
                  placeholder="Tell us about yourself or your release plans — helps us verify faster."
                  className="w-full bg-white/[0.05] border border-white/10 focus:border-[#FF2E00] rounded-xl px-4 py-2.5 text-sm outline-none transition resize-none"
                />
                <p className="text-[11px] text-white/40 mt-1 text-right">{form.statement.length} / 600</p>
              </div>

              {status?.has_pending_claim && !status?.my_claim && (
                <div className="bg-amber-500/10 border border-amber-500/30 rounded-xl px-3 py-2 text-amber-200 text-xs flex items-start gap-2">
                  <AlertCircle size={14} className="shrink-0 mt-0.5" />
                  Another user has a pending claim on this page. We&apos;ll review all claims together.
                </div>
              )}

              <div className="flex items-center gap-2 pt-2">
                <button
                  data-testid="claim-submit-btn"
                  type="submit"
                  disabled={busy || !form.official_email}
                  className="bg-[#FF2E00] hover:bg-[#E62900] disabled:opacity-50 disabled:cursor-not-allowed text-white font-semibold rounded-full px-5 py-2.5 text-sm flex items-center gap-2 transition hover:shadow-[0_0_24px_rgba(255,51,102,0.4)]"
                >
                  {busy ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
                  {busy ? "Submitting…" : "Submit claim"}
                </button>
                <button
                  type="button"
                  onClick={() => setOpen(false)}
                  disabled={busy}
                  className="text-white/55 hover:text-white text-sm rounded-full px-4 py-2.5"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </>
  );
};

export default ClaimArtistDialog;
